// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { UnitOfMeasureModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText from "@insite/content-library/Components/SmallHeadingAndText";
import { Typography } from "@insite/mobius/index";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";

interface OwnProps extends SelectProps {
    labelOverride?: React.ReactNode;
    disabled?: boolean;
    extendedStyles?: SelectPresentationProps;
    // XNG-Changes
    showUom?: boolean;
    isPdp?: boolean;
    displayPack?: boolean;
}

export interface ProductUnitOfMeasureSelectStyles extends SelectPresentationProps {
    // Xng-Changes:
    uomLabel?: TypographyProps;
    printValueText?: TypographyProps;
    attribute?: TypographyProps;
    wrapper?: InjectableCss;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export const productUnitOfMeasureSelectStyles: ProductUnitOfMeasureSelectStyles = {};

const ProductUnitOfMeasureSelect: React.FC<Props> = ({
    // productContext: {
    //     product: { unitOfMeasures },
    //     productInfo: { unitOfMeasure, productId },
    //     onUnitOfMeasureChanged,
    // },
    productContext,
    disabled,
    productSettings,
    labelOverride,
    extendedStyles,
    // XNG-Changes
    showUom,
    isPdp,
    displayPack,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeToNew(productUnitOfMeasureSelectStyles, extendedStyles));

    // <XNG-Changes>
    let unitOfMeasures: UnitOfMeasureModel[] | null = [];
    let unitOfMeasure = "";
    let productId = "";
    if (productContext.product) {
        unitOfMeasures = productContext.product.unitOfMeasures;
    }
    if (productContext.productInfo) {
        unitOfMeasure = productContext.productInfo.unitOfMeasure;
        productId = productContext.productInfo.productId;
    }

    // XNG-Changes: classic sorting
    const sortedUnitOfMeasures = React.useMemo(() => {
        if (!unitOfMeasures) {
            return [];
        }

        return [...unitOfMeasures].sort((a: UnitOfMeasureModel, b: UnitOfMeasureModel) => {
            return a?.qtyPerBaseUnitOfMeasure - b?.qtyPerBaseUnitOfMeasure;
        });
    }, [unitOfMeasures]);

    // </XNG-Changes>

    if (
        !showUom &&
        (!productSettings?.alternateUnitsOfMeasure ||
            !unitOfMeasures ||
            unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1)
    ) {
        return null;
    }

    const uomChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!productContext.onUnitOfMeasureChanged) {
            logger.warn(`There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`);
            return;
        }
        productContext.onUnitOfMeasureChanged(event.target.value);
    };

    // Xng-Changes: add print label and value
    if (isPdp) {
        return (
            <StyledWrapper data-test-selector="unitOfMeasures_wrapper">
                <Typography
                    {...styles.attribute}
                    data-test-selector={`selected_unitOfMeasure_${unitOfMeasure.replace(/\s+/g, "_")}`}
                >
                    {translate(`Length: ${unitOfMeasure}`)}
                </Typography>
                <StyledWrapper {...styles.wrapper}>
                    {sortedUnitOfMeasures?.map(uom => (
                        <StyledWrapper
                            key={uom.id}
                            onClick={() => {
                                if (!productContext.onUnitOfMeasureChanged) {
                                    logger.warn(
                                        `There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`,
                                    );
                                    return;
                                }
                                productContext.onUnitOfMeasureChanged(uom.unitOfMeasure);
                            }}
                            className={unitOfMeasure === uom.unitOfMeasure ? "selected" : ""}
                            data-test-selector="unitOfMeasure_element"
                        >
                            <Typography>
                                {uom.description ? uom.description : uom.unitOfMeasureDisplay}
                                {uom.qtyPerBaseUnitOfMeasure !== 1 ? `/${uom.qtyPerBaseUnitOfMeasure}` : ""}
                            </Typography>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
                {displayPack && productContext.product.packDescription && (
                    <SmallHeadingAndText heading={translate("Pack")} text={productContext.product.packDescription} />
                )}
            </StyledWrapper>
        );
    }
    return (
        <>
            <Select
                label={labelOverride ?? translate("U/M")}
                value={unitOfMeasure}
                onChange={uomChangeHandler}
                disabled={disabled}
                data-test-selector="product_unitOfMeasureSelect"
                {...styles}
                {...otherProps}
            >
                {/* XNG-Change use sortedUnitOfMeasures */}
                {sortedUnitOfMeasures?.map(uom => (
                    <option key={uom.id.toString()} value={uom.unitOfMeasure}>
                        {uom.description ? uom.description : uom.unitOfMeasureDisplay}{" "}
                        {uom.qtyPerBaseUnitOfMeasure !== 1 ? `/${uom.qtyPerBaseUnitOfMeasure}` : ""}
                    </option>
                ))}
                {/* XNG-Changes */}
                {!sortedUnitOfMeasures.length && <option value="" />}
            </Select>
            {displayPack && productContext.product.packDescription && (
                <SmallHeadingAndText heading={translate("Pack")} text={productContext.product.packDescription} />
            )}
        </>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductUnitOfMeasureSelect));
